import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/contexts/ToastContext';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useProducts } from '@/contexts/ProductsContext';
import { trackAddToCart } from '@/lib/tracking';

interface CrossSellProps {
  currentSlug: string;
}

export default function CrossSell({ currentSlug }: CrossSellProps) {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { addToCart, openCart } = useCart();
  const { showToast } = useToast();
  const { products, routineSet } = useProducts();

  const others = products.filter((p) => p.id !== currentSlug).slice(0, 2);

  const handleAdd = (slug: string, name: string, price: string) => {
    addToCart(slug, 1);
    trackAddToCart({ id: slug, name, price: Number(price.replace(/[^0-9.]/g, '')), quantity: 1 });
    showToast(
      t('cart.added', 'Added to Cart ✓'),
      t('cart.viewCart', 'View Cart →'),
      openCart,
    );
  };

  const stepColor: Record<string, string> = {
    open: '#1B3A5C',
    treat: '#7B1113',
    lock: '#2D6A4F',
  };

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6]">
      <h3 className="text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A] mb-6">
        {t('products.crossSell')}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {others.map((p) => (
          <div key={p.id} className="flex flex-col gap-3 p-4 rounded-xl bg-[#FAFAF9] border border-[#F0EFED]">
            <Link to={`${prefix}/products/${p.id}`} className="flex items-center gap-3 cursor-pointer">
              <div className="w-14 h-14 rounded-lg bg-white flex-shrink-0 overflow-hidden">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] tracking-[0.1em] uppercase font-medium" style={{ color: stepColor[p.id] || '#7B1113' }}>
                  {p.stepKeyword} · {p.volume}
                </span>
                <span className="text-xs font-medium text-[#2A2A2A] leading-tight">{p.name}</span>
                <span className="text-xs text-[#8A8A8A] font-light">{p.price}</span>
              </div>
            </Link>
            <button
              onClick={() => handleAdd(p.id, p.name, p.price)}
              className="w-full py-2.5 bg-[#7B1113] text-white text-[10px] tracking-[0.12em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
            >
              {t('common.addToCart')}
            </button>
          </div>
        ))}

        {/* 3-Step Set card */}
        {routineSet && (
          <div className="flex flex-col gap-3 p-4 rounded-xl bg-[#1A1A1A] border border-[#1A1A1A]">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-white">
                <img src={routineSet.image} alt={routineSet.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] tracking-[0.1em] uppercase text-white/40 font-medium">SET</span>
                <span className="text-xs font-medium text-white leading-tight">{routineSet.name}</span>
                <span className="text-xs text-white font-medium">{routineSet.price} <span className="text-white/40 line-through text-[10px]">{routineSet.originalPrice}</span></span>
              </div>
            </div>
            <button
              onClick={() => handleAdd('3-step-set', routineSet.name, routineSet.price)}
              className="w-full py-2.5 bg-white text-[#7B1113] text-[10px] tracking-[0.12em] uppercase rounded-full hover:bg-white/90 transition-colors cursor-pointer text-center font-medium"
            >
              {t('common.addToCart')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}